import React, { useState } from "react";
import {
  Option,
  BaselineDefaultsRentControlFormDefaults,
  BaselineDefaultsRentControlFormPropertyType,
  BaselineDefaultsRentControlFormRentControlType,
  DefaultsPageConfiguration
} from "api";
import createDataTable, {
  IPagedTableState,
  ColumnDefinition,
  DefaultInputEditorData,
  DataTableProps
} from "components/controls/createTable";

import Dropdown from "components/controls/Dropdown";
import listUtils from "utils/listUtils";
import rentControlTypeUtils from "utils/rentControlTypeUtils";
import OverlayPanel from "components/controls/OverlayPanel";
import ValidationWrapper from "components/controls/ValidationWrapper";
import { InputText } from "primereact/inputtext";

export interface BaselineDefaultsRentControlFormTableProps {
  defaults?: BaselineDefaultsRentControlFormDefaults[] | undefined;
  properties: BaselineDefaultsRentControlFormPropertyType[];
  rentControlTypes: BaselineDefaultsRentControlFormRentControlType[];
  onChange: (defaults: BaselineDefaultsRentControlFormDefaults[]) => void;
  hideRemoveRowColumn?: boolean | undefined;
  paging?: boolean | undefined;
  onAddRowAction?: (() => void) | undefined;
  includeRemoveRowColumn?: "show" | "buffer" | undefined;
  hideUnitNumberColumn?: boolean;
  pageConfiguration: DefaultsPageConfiguration | undefined;
}

interface TableState extends IPagedTableState {
  divisionFilter?: Option;
  sortField?: any;
  sortOrder?: any;
  sortState?: "unsorted" | "asc" | "desc" | undefined;
}

const BaselineDefaultsRentControlFormTable: React.FC<BaselineDefaultsRentControlFormTableProps> =
  function (props: BaselineDefaultsRentControlFormTableProps) {
    const [table, setTable] = useState<TableState>({
      offset: 0,
      limit: 10,
      sortOrder: 1
    });

    const norm = (v: string | null | undefined) => v || undefined;

    const rowHasChanged = (
      oldRow: BaselineDefaultsRentControlFormDefaults,
      newRow: BaselineDefaultsRentControlFormDefaults
    ): boolean =>
      oldRow.selectedPropertyType?.key !== newRow.selectedPropertyType?.key ||
      oldRow.selectedRentControlType?.key !==
        newRow.selectedRentControlType?.key ||
      norm(oldRow.unitNumber) !== norm(newRow.unitNumber) ||
      norm(oldRow.note) !== norm(newRow.note);

    const onRowChanged = (
      oldRow: BaselineDefaultsRentControlFormDefaults,
      newRow: BaselineDefaultsRentControlFormDefaults
    ) => {
      if (!props.defaults) {
        return;
      }

      if (!rowHasChanged(oldRow, newRow)) {
        return;
      }

      newRow.isChanged = true;

      props.onChange(
        listUtils.copyListWithNewItem(props.defaults, oldRow, newRow)
      );
    };

    const onRemoveRow = (row: BaselineDefaultsRentControlFormDefaults) => {
      if (!props.defaults) {
        return;
      }

      const newDefaults = listUtils.copyListWithItemRemoved(
        props.defaults,
        row
      );
      props.onChange(newDefaults);
    };

    const onDefaultsRentControlFormPropertyChange = (
      defaultsRow: BaselineDefaultsRentControlFormDefaults
    ) => {
      return (
        selectedProperty:
          | BaselineDefaultsRentControlFormPropertyType
          | undefined
      ) => {
        const newDefaultsRow: BaselineDefaultsRentControlFormDefaults = {
          ...defaultsRow,
          selectedPropertyType: selectedProperty,
          county: selectedProperty && selectedProperty.county
        };

        onRowChanged(defaultsRow, newDefaultsRow);
      };
    };

    const getPropertyDefaultsBody = function (rowData: any) {
      const defaults = rowData as BaselineDefaultsRentControlFormDefaults;

      let dropDown = (
        <Dropdown
          onChange={onDefaultsRentControlFormPropertyChange(defaults)}
          selectedValue={defaults.selectedPropertyType}
          options={props.properties}
          style={{ minWidth: "100%", maxWidth: "100%" }}
          validationMessage={
            defaults.fieldValidationErrors &&
            defaults.fieldValidationErrors["SelectedPropertyType"]
          }
        />
      );

      let label = (
        <div>
          {defaults.selectedPropertyType &&
            defaults.selectedPropertyType.displayValue}
        </div>
      );

      if (props.pageConfiguration && props.pageConfiguration.isEditEnabled) {
        return dropDown;
      } else {
        return label;
      }
    };

    const onDefaultsRentControlTypeChange = (
      defaultsRow: BaselineDefaultsRentControlFormDefaults
    ) => {
      return (
        selectedRentControlType:
          | BaselineDefaultsRentControlFormRentControlType
          | undefined
      ) => {
        onRowChanged(defaultsRow, {
          ...defaultsRow,
          selectedRentControlType: selectedRentControlType
        });
      };
    };

    const getRentControlTypeBody = function (rowData: any) {
      const defaults = rowData as BaselineDefaultsRentControlFormDefaults;

      const dropDown = (
        <Dropdown
          onChange={onDefaultsRentControlTypeChange(defaults)}
          selectedValue={defaults.selectedRentControlType}
          options={props.rentControlTypes}
          style={{ minWidth: "100%", maxWidth: "100%" }}
          validationMessage={
            defaults.fieldValidationErrors &&
            defaults.fieldValidationErrors["SelectedRentControlType"]
          }
        />
      );

      const label = (
        <div>
          {defaults.selectedRentControlType &&
            defaults.selectedRentControlType.displayValue}
        </div>
      );

      return (
        <div style={{ display: "flex", alignItems: "center" }}>
          <div style={{ flexGrow: 1, minWidth: 0 }}>
            {props.pageConfiguration && props.pageConfiguration.isEditEnabled
              ? dropDown
              : label}
          </div>
          {getLimitHistoryOverlay(defaults)}
        </div>
      );
    };

    const getRentControlTypeForRow = function (
      defaults: BaselineDefaultsRentControlFormDefaults
    ) {
      return props.rentControlTypes.find(
        (rentControlType) =>
          rentControlType.key === defaults.selectedRentControlType?.key
      );
    };

    /*
     * A row on a batch created before the rent control types existed holds its own maximum percentages and selects no
     * rent control type. Everything shown for it is read from the row itself.
     */
    const getLimitForRow = function (
      defaults: BaselineDefaultsRentControlFormDefaults
    ) {
      const rentControlType = getRentControlTypeForRow(defaults);

      return rentControlType
        ? rentControlTypeUtils.getLimitAsOfToday(rentControlType)
        : rentControlTypeUtils.getLimitFromRow(defaults);
    };

    /*
     * The columns show what applies today. The overlay shows every effective date, because a rent control type can have
     * allowed increases that have not taken effect yet or that applied before today. A row that has no rent control
     * type shows the single set of values stored on it.
     */
    const getLimitHistoryOverlay = function (
      defaults: BaselineDefaultsRentControlFormDefaults
    ) {
      const rentControlType = getRentControlTypeForRow(defaults);
      const limitFromRow = rentControlTypeUtils.getLimitFromRow(defaults);

      const limits = rentControlType
        ? rentControlTypeUtils.getLimitsInEffectiveDateOrder(rentControlType)
        : limitFromRow
        ? [limitFromRow]
        : [];

      if (limits.length === 0) {
        return <div />;
      }

      const asOfToday = rentControlTypeUtils.getLimitAsOfToday(rentControlType);

      return (
        <OverlayPanel
          buttonTitle={""}
          buttonIcon={"pi pi-info-circle"}
          buttonStyle={{ fontSize: "1.1em" }}
        >
          <div style={{ whiteSpace: "nowrap" }}>
            <b>{rentControlType?.displayValue ?? "Stored on this batch"}</b>
            <table style={{ marginTop: "8px" }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left", paddingRight: "12px" }}>
                    Effective date
                  </th>
                  <th style={{ textAlign: "right", paddingRight: "12px" }}>
                    Max % by RM
                  </th>
                  <th style={{ textAlign: "right", paddingRight: "12px" }}>
                    Max % by law
                  </th>
                  <th style={{ textAlign: "right" }}>Increases allowed</th>
                </tr>
              </thead>
              <tbody>
                {limits.map((limit, index) => (
                  <tr
                    key={limit.key || index}
                    style={{
                      fontWeight: limit === asOfToday ? "bold" : "normal"
                    }}
                  >
                    <td style={{ paddingRight: "12px" }}>
                      {limit.effectiveDate || "Any date"}
                    </td>
                    <td style={{ textAlign: "right", paddingRight: "12px" }}>
                      {rentControlTypeUtils.getPercentageLabel(
                        limit.maxPercentageByRevenueManager
                      )}
                    </td>
                    <td style={{ textAlign: "right", paddingRight: "12px" }}>
                      {rentControlTypeUtils.getPercentageLabel(
                        limit.maxPercentageByLaw
                      )}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {rentControlTypeUtils.getIncreasesAllowedLabel(limit)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </OverlayPanel>
      );
    };

    const getMaxPercentageByLawBody = function (rowData: any) {
      const defaults = rowData as BaselineDefaultsRentControlFormDefaults;
      const limit = getLimitForRow(defaults);

      return (
        <div>
          {rentControlTypeUtils.getPercentageLabel(limit?.maxPercentageByLaw)}
        </div>
      );
    };

    /*
     * A row from before the rent control types existed had no limit on the number of increases, so there is nothing to
     * show for it rather than two blank numbers.
     */
    const getIncreasesAllowedBody = function (rowData: any) {
      const defaults = rowData as BaselineDefaultsRentControlFormDefaults;

      if (!getRentControlTypeForRow(defaults)) {
        return <div>{rentControlTypeUtils.notConfiguredLabel}</div>;
      }

      const limit = rentControlTypeUtils.getLimitAsOfToday(
        getRentControlTypeForRow(defaults)
      );

      return <div>{rentControlTypeUtils.getIncreasesAllowedLabel(limit)}</div>;
    };

    const getNoteBody = function (rowData: any) {
      const defaults = rowData as BaselineDefaultsRentControlFormDefaults;

      const editor = (
        <ValidationWrapper
          showErrorState={
            defaults.fieldValidationErrors != null &&
            defaults.fieldValidationErrors["Note"] != null
          }
        >
          <InputText
            value={defaults.note ?? ""}
            maxLength={50}
            tooltip={
              defaults.fieldValidationErrors &&
              defaults.fieldValidationErrors["Note"]
            }
            tooltipOptions={{ event: "hover" }}
            onChange={(e) => {
              onRowChanged(defaults, {
                ...defaults,
                note: e.currentTarget.value || undefined
              });
            }}
          />
        </ValidationWrapper>
      );

      const label = <div>{defaults.note}</div>;

      if (props.pageConfiguration && props.pageConfiguration.isEditEnabled) {
        return editor;
      } else {
        return label;
      }
    };

    const getDefaultInputEditorData =
      function (): DefaultInputEditorData<BaselineDefaultsRentControlFormDefaults> {
        return {
          isEditDisabled: !(
            props.pageConfiguration && props.pageConfiguration.isEditEnabled
          ),
          onChange: (
            row: BaselineDefaultsRentControlFormDefaults,
            field: keyof BaselineDefaultsRentControlFormDefaults,
            value: any
          ) => {
            const newDefaultsRow = {
              ...row
            };

            newDefaultsRow[field] = value;
            onRowChanged(row, newDefaultsRow);
          }
        };
      };

    const getColumns = function (): any[] {
      let columns = [] as ColumnDefinition<
        BaselineDefaultsRentControlFormDefaults,
        keyof BaselineDefaultsRentControlFormDefaults
      >[];

      const dropdownColumnStyle = { width: "13%" };

      columns = columns.concat([
        {
          field: "county",
          header: "County",
          style: { width: "7%", textAlign: "center" }
        },
        {
          header: "Property",
          style: { ...dropdownColumnStyle, width: "18%" },
          body: getPropertyDefaultsBody
        }
      ]);

      if (!props.hideUnitNumberColumn) {
        columns = columns.concat([
          {
            field: "unitNumber",
            header: "Unit #",
            style: { width: "8%", textAlign: "center" },
            defaultInputEditorData: getDefaultInputEditorData()
          }
        ]);
      } else {
        columns = columns.concat([
          {
            field: "unitNumber",
            header: "",
            style: { width: "8%", textAlign: "center" }
          }
        ]);
      }

      columns = columns.concat([
        {
          header: "Rent control type",
          style: { ...dropdownColumnStyle, width: "21%" },
          body: getRentControlTypeBody
        },
        {
          header: "Max % by law (as of today)",
          style: { width: "9%", textAlign: "center" },
          body: getMaxPercentageByLawBody
        },
        {
          header: "Increases allowed (as of today)",
          style: { width: "9%", textAlign: "center" },
          body: getIncreasesAllowedBody
        },
        {
          field: "note",
          header: "Note",
          style: { width: "9%", textAlign: "center" },
          body: getNoteBody
        },
        {
          field: "lastUpdatedDate",
          header: "Last updated date",
          style: { width: "9%", textAlign: "center" }
        },
        {
          field: "lastUpdatedBy",
          header: "Last updated by",
          style: { width: "8%", textAlign: "center" }
        }
      ]);

      return columns;
    };

    const tableProps: DataTableProps<
      BaselineDefaultsRentControlFormDefaults,
      TableState
    > = {
      value: props.defaults || undefined,
      editMode: "cell",
      state: table,
      updateState: setTable,
      paging: props.paging,
      // TODO: This functionality only works when added at this leve. I was unable to get it to work when I moved it up to the createDataTable function
      sortField: table.sortField,
      sortOrder: table.sortOrder,
      onSort: (e: any) => {
        //clear sort column
        if (
          table.sortOrder === -1 &&
          e.sortOrder === 1 &&
          table.sortField === e.sortField
        ) {
          setTable({
            ...table,
            sortField: undefined,
            sortOrder: undefined
          });
        } else {
          setTable({
            ...table,
            sortField: e.sortField,
            sortOrder: e.sortOrder
          });
        }
      },
      onAddRowAction: props.onAddRowAction,
      onRemoveRowAction:
        props.includeRemoveRowColumn === "show" ? onRemoveRow : undefined,
      hasRemoveRowBuffer: props.includeRemoveRowColumn === "buffer",
      emptyMessage: "No defaults have been added."
    };
    const dataTable = createDataTable<
      BaselineDefaultsRentControlFormDefaults,
      TableState
    >(getColumns(), tableProps);

    return <div>{dataTable}</div>;
  };

export default BaselineDefaultsRentControlFormTable;
